import { createSelector } from 'reselect';

import appSettings from 'wanderu-config';
import {
    mapCarrierData,
    mapInfoSectionContent,
} from 'wanderu-ducks-seo';

import { mapAmenities } from '../../utils';
import SLICE_NAMESPACE from '../types';
import NAMESPACE from './types';

const getStaticAssetsUrl = () => {
    return appSettings.config && appSettings.config.s3.staticAssetsUrl;
};

const selectData = (state) => {
    return state[SLICE_NAMESPACE][NAMESPACE];
};

const selectPageData = (state, stationId = '') => {
    return selectData(state)[stationId];
};

const getContent = (state, stationId = '') => {
    return selectPageData(state, stationId)?.content || {};
};

const getCarriers = (state, stationId = '') => {
    return selectPageData(state, stationId)?.carriers;
};

const getImages = (state, stationId = '') => {
    return selectPageData(state, stationId)?.images;
};

const getLocation = (state, stationId = '') => {
    return selectPageData(state, stationId)?.location || {};
};

const getStationImages = (state, stationId = '') => {
    const wid = stationId.split('-')[2];

    return getImages(state, stationId)?.station[wid] || [];
};

const getStationMapImages = (state, stationId = '') => {
    const wid = stationId.split('-')[2];

    return getImages(state, stationId)?.stationMaps[wid] || {};
};

const getNearbyContent = (state, stationId = '') => {
    return selectPageData(state, stationId)?.nearby || [];
};

const selectStats = (state, stationId = '') => {
    return selectPageData(state, stationId)?.stats || {};
};

const getRoutes = (state, stationId = '') => {
    return selectPageData(state, stationId)?.c2c || [];
};

const selectRoutes = createSelector(
    getRoutes,
    (routes) => {
        return routes.map((route) => ({
            avgPrice: route.avg_price_usd,
            avgTime: route.avg_time_min,
            dailyTrips: route.trips_per_day,
            destination: route.arrive_city_name,
            url: route.c2c_url,
        }));
    },
);

const selectLocation = createSelector(
    getLocation,
    (location) => ({
        ...location,
        formattedAddress: `${location.street1}, ${location.street2
            ? location.street2 + ', '
            : ''}${location.city}, ${location.stateCode}, ${location.countryCode}`,
    }),
);

const selectStationImages = createSelector(
    getStationImages,
    getStationMapImages,
    (images = [], maps = {}) => {
        const allImages = [
            ...images,
            ...maps?.location || [],
        ];

        return allImages.map((image) => ({
            ...image,
            url: getStaticAssetsUrl() + image.url,
        }));
    },
);

const selectStationMaps = createSelector(
    getStationMapImages,
    (maps = {}) => {
        const { layout = [] } = maps;

        return layout.map((image) => ({
            ...image,
            url: getStaticAssetsUrl() + image.url,
        })) || [];
    },
);

const selectMetaImage = createSelector(
    selectStationImages,
    (stationImages) => {
        const metaImage = stationImages[0] || {};

        return {
            height: metaImage.height,
            src: metaImage.url,
            width: metaImage.width,
        };
    },
);

const selectNearbyContent = createSelector(
    getNearbyContent,
    (nearby) => {
        return nearby.map((content) => ({
            stat: content.distance,
            text: content.title,
        }));
    },
);

const getCarrierContent = createSelector(
    getCarriers,
    (carrierData) => {
        if (!carrierData) {
            return undefined;
        }

        return carrierData.reduce((acc, carrier) => {
            Object.entries(carrier.content)
                .forEach(([key, value]) => {
                    if (key.includes(carrier.code)) {
                        acc[key] = value;
                    }

                    acc[`${carrier.code}_${key}`] = value;
                });
            return acc;
        }, {});
    },
);

const selectCarriers = createSelector(
    getCarriers,
    mapCarrierData,
);

const selectContent = createSelector(
    getContent,
    getCarrierContent,
    (content, carrierContent = {}) => ({
        ...content,
        ...carrierContent,
    }),
);

const selectFaqContent = createSelector(
    selectContent,
    mapInfoSectionContent,
);

const selectAmenities = createSelector(
    selectContent,
    mapAmenities,
);

const selectFaqStructuredContent = createSelector(
    selectFaqContent,
    (faqs) => {
        const faqStructuredDataList = faqs.reduce((arr, info) => {
            if (!info.title || !info.text) {
                return arr;
            }

            return [
                ...arr,
                {
                    '@type': 'Question',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: info.text,
                    },
                    name: info.title,
                },
            ];
        }, []);

        return {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqStructuredDataList,
        };
    },
);

const selectContentStructuredData = createSelector(
    selectContent,
    selectLocation,
    (content, location) => {
        const type = location.primaryTransMode === 'bus'
            ? 'BusStation'
            : 'TrainStation';

        return {
            '@context': 'https://schema.org',
            '@type': type,
            description: 'introDescription',
            isAccessibleForFree: true,
            latitude: location.latitude,
            longitude: location.longitude,
            name: content.stationName,
            publidAccess: true,
            url: `https://www.wanderu.com${content.canonicalSlug}`,
        };
    },
);

const selectStructuredData = createSelector(
    selectContentStructuredData,
    selectFaqStructuredContent,
    (contentStructuredData, faqStructuredData) => {
        return [contentStructuredData, faqStructuredData];
    },
);

const selectors = {
    selectAmenities,
    selectCarriers,
    selectContent,
    selectFaqContent,
    selectLocation,
    selectMetaImage,
    selectNearbyContent,
    selectRoutes,
    selectStationImages,
    selectStationMaps,
    selectStats,
    selectStructuredData,
};

export default selectors;
